import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getObservabilityConsumer } from '@atlassian/rovo-content-bridge-api/observability';
import type { BridgeObservabilityEvent } from '@atlassian/rovo-content-bridge-api/observability-types';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { dispatchBridgeObservabilitySignal } from './dispatchBridgeObservabilitySignal.ts';

/**
 * Surfaces Rovo content-bridge health from the Confluence (host) side of the AIFC BE→FE bridge.
 *
 * The registration lifecycle drives the `aifc-bridge-registration` Experience so a client whose
 * iframe mounts but never registers — leaving streamed content unconsumed, as in HOT-303387 —
 * fails the experience and its SignalFx detector, rather than staying invisible while the backend
 * looks healthy. Queue backpressure and drops are forwarded as operational analytics.
 *
 * Bridge signals arrive on the global emitter (including a timer-driven gap), so they are queued and
 * replayed to {@link dispatchBridgeObservabilitySignal} inside a React effect, keeping tracker calls
 * on the deterministic lifecycle the tracker requires.
 */
export const useAifcBridgeObservability = (): void => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const queueRef = useRef<BridgeObservabilityEvent[]>([]);
	const [drainToken, scheduleDrain] = useReducer((token: number) => token + 1, 0);

	const enqueueSignal = useCallback((event: BridgeObservabilityEvent) => {
		queueRef.current.push(event);
		scheduleDrain();
	}, []);

	useEffect(() => {
		const queued = queueRef.current;
		queueRef.current = [];
		for (const event of queued) {
			dispatchBridgeObservabilitySignal(event, {
				experienceTracker,
				createAnalyticsEvent,
				submitError: (error) =>
					getMonitoringClient().submitError(error, { attribution: Attribution.CONTENT_TYPES }),
			});
		}
	}, [drainToken, experienceTracker, createAnalyticsEvent]);

	useEffect(() => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (!fg('confluence_aifc_bridge_observability')) {
			return;
		}
		const consumer = getObservabilityConsumer();
		consumer.enable(enqueueSignal);
		return () => consumer.disable();
	}, [enqueueSignal]);
};
